import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { FormatUtils, PaletteUtils, cn } from '@shared/utils';
import { CardKind, type CardsResponse, CardStatus } from '@shared/api';
import { Icon, Text } from '@shared/widgets';
import VisaIcon from '@assets/icons/card/visa.svg';
import MastercardIcon from '@assets/icons/card/mastercard.svg';
import PinstripeIcon from '@assets/icons/card/pinstripe.svg';

interface CardItemProps {
  className?: string;
  withHover?: boolean;
  card: CardsResponse;
}

export const CardItem = (props: CardItemProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('accounts');

  const closed = props.card.status === CardStatus.CLOSED;
  const blocked = props.card.status === CardStatus.BLOCKED || props.card.status === CardStatus.FROZEN;
  const disabled = props.card.status === CardStatus.DELINQUENT || props.card.status === CardStatus.INACTIVE;
  const viewCardDetails = useMemo(
    () => profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === props.card.cardId)?.viewCardDetails,
    [profileFeatureStore.data?.cardFlows, props.card],
  );
  return (
    <div
      className={cn(
        'group relative mx-[13px] mb-[30px] shrink-0',
        props.withHover && 'transition-transform duration-200 hover:scale-110',
        viewCardDetails === false && 'cursor-not-allowed',
        props.className,
      )}
      title={viewCardDetails === false ? t('card.viewCardDetails') : undefined}
    >
      <div
        className={cn(
          'relative flex h-[103px] w-[176px] flex-col overflow-hidden rounded-lg border border-[#3d3d3d] bg-soft-black bg-cover bg-center bg-no-repeat p-3 text-white',
          closed && 'border-[#5c5c5c] bg-slate',
          blocked && 'border-red',
          disabled && 'border-[#bebebe] bg-[#b7b7b7]',
          PaletteUtils.isVcfcPalette && 'bg-primary text-ice',
          PaletteUtils.isVcfcPalette && [
            (props.card.status === CardStatus.BLOCKED || props.card.status === CardStatus.DELINQUENT) && 'bg-soft-black',
            closed && 'bg-slate',
            props.card.status === CardStatus.INACTIVE && '!bg-light-gray',
          ],
        )}
      >
        {PaletteUtils.isInsigniaPalette && (
          <>
            <svg
              className={cn('absolute right-[-16px] top-[-36px] fill-white opacity-[0.02]', (disabled || closed) && 'fill-black')}
              width='122'
              height='87'
              fill='transparent'
              viewBox='0 0 122 87'
              xmlns='http://www.w3.org/2000/svg'
            >
              <ellipse cx='61' cy='43.5' rx='60' ry='42.5' />
            </svg>
            <svg
              className={cn('absolute bottom-[-38px] left-[-36px] fill-white opacity-[0.02]', (disabled || closed) && 'fill-black')}
              width='122'
              height='87'
              fill='transparent'
              viewBox='0 0 122 87'
              xmlns='http://www.w3.org/2000/svg'
            >
              <ellipse cx='61' cy='43.5' rx='60' ry='42.5' />
            </svg>
          </>
        )}
        {PaletteUtils.isVcfcPalette && <Icon icon={<PinstripeIcon />} className='absolute top-0' />}
        <ul className='-mt-[5px] flex min-h-[20px]'>
          <Text
            className={cn(
              'text-[8px] tracking-[0.03em] opacity-50',
              'after:relative after:top-[-2px] after:mx-1 after:inline-block after:h-[2px] after:w-[2px] after:rounded-full after:bg-current after:opacity-50',
              disabled && ['text-ice opacity-100', 'after:bg-ice after:opacity-100'],
            )}
            tag='li'
            uppercase
            weight='medium'
            color='white'
          >
            {t(`card.type.${props.card.cardType.toLowerCase()}`)}
          </Text>
          <Text
            className={cn('text-[8px] tracking-[0.03em] opacity-50', disabled && 'text-ice opacity-100', blocked && 'text-red opacity-100')}
            tag='li'
            uppercase
            weight='medium'
            color='white'
          >
            {t(`statuses.card.${props.card.status}`, { ns: 'common' })}
          </Text>
        </ul>
        <div className='mt-auto flex items-end'>
          <div className='flex-auto'>
            <Text
              className={cn('mb-1 whitespace-nowrap text-[8px] tracking-[0.03em] opacity-50', disabled && 'text-ice opacity-100')}
              weight='medium'
              color='white'
            >
              {FormatUtils.toSliceNumber(props.card.cardNumber, 16)}
            </Text>
            <Text className='overflow-hidden text-ellipsis whitespace-nowrap text-[10px]' weight='medium' color='white'>
              {props.card.cardHolder}
            </Text>
          </div>
          {props.card.cardKind === CardKind.Credit && <Icon icon={<VisaIcon />} color='white' />}
          {props.card.cardKind === CardKind.Debit && <Icon icon={<MastercardIcon className='h-[27.5px] w-full' />} />}
        </div>
      </div>
      {props.withHover && (
        <Text
          className={cn(
            'absolute left-1/2 top-full -translate-x-1/2 whitespace-nowrap text-center opacity-0 transition-opacity duration-200',
            'group-hover:opacity-100',
          )}
          size='sm'
        >
          Click to view details
        </Text>
      )}
    </div>
  );
};
